import { isNativeApp } from './platform';
import { getInsightsArticle } from '../data/insightsArticles';

const SITE_NAME = 'ウラカルテ';
// ネイティブアプリ内の origin は capacitor://localhost 等になり共有URLとして無効なため、
// 共有・OGP用のURLは常に公開Webのオリジンで組み立てる
const PUBLIC_WEB_ORIGIN = 'https://urakarte.app';
const DEFAULT_TITLE = `${SITE_NAME} - 美容口コミの信頼性を、エビデンスで。`;
const DEFAULT_DESCRIPTION =
  '美容・スキンケアの口コミをAIで分析。エビデンス提出で信頼性を高め、検索・フィードで自分に合ったレビューを見つけられます。';

const META_STORE = new Map();

function upsertMeta(selector, attr, key, content) {
  if (typeof document === 'undefined') return;
  let el = document.querySelector(selector);
  if (!content) {
    if (el) el.removeAttribute(attr === 'name' ? 'content' : 'content');
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  META_STORE.set(`${attr}:${key}`, content);
}

export function getPublicOrigin() {
  if (isNativeApp()) return PUBLIC_WEB_ORIGIN;
  if (typeof window === 'undefined') return '';
  return window.location.origin;
}

export function buildAbsoluteUrl(path = '/') {
  const origin = getPublicOrigin();
  if (!origin) return path;
  return `${origin}${path.startsWith('/') ? path : `/${path}`}`;
}

export function buildOgImageUrl() {
  return buildAbsoluteUrl('/og-image.png?v=2');
}

/**
 * URLの%エンコードされた日本語を読める形に戻す（コピー・共有用）。
 * ブラウザに貼ると自動で再エンコードされるため、機能上も問題ない。
 * 不正なエンコードで decodeURI が throw する場合は元のURLを返す。
 */
export function toDisplayUrl(url) {
  try {
    // 空白はURLの区切りとして誤認されるため %20 のまま残す
    return decodeURI(url).replace(/ /g, '%20');
  } catch {
    return url;
  }
}

function decodePathSegment(pathname, prefix) {
  if (!pathname.startsWith(prefix)) return '';
  try {
    return decodeURIComponent(pathname.slice(prefix.length).split('/')[0] || '').trim();
  } catch {
    return (pathname.slice(prefix.length).split('/')[0] || '').trim();
  }
}

function resolveMeta(pathname, search) {
  const params = new URLSearchParams(search || '');
  const query = params.get('query') || params.get('q') || '';

  if (pathname === '/' || pathname === '/feed') {
    return { title: DEFAULT_TITLE, description: DEFAULT_DESCRIPTION, canonicalPath: pathname };
  }
  if (pathname === '/earn') {
    return {
      title: `自分の情報を収益化したい？ | ${SITE_NAME}`,
      description:
        '調べて体験した貴重な情報、タダで渡したくない？自分のツイートをウラカルテに登録すると、閲覧数に応じてポイントが貯まり、現金にかえられます。',
      canonicalPath: '/earn',
    };
  }
  if (pathname === '/about') {
    return {
      title: `運営について | ${SITE_NAME}`,
      description:
        'ウラカルテの運営目的・団体情報・信頼性の仕組みをご紹介。美容口コミの信頼性をエビデンスで可視化する検索サービスです。',
      canonicalPath: '/about',
    };
  }
  if (pathname === '/procedures') {
    return {
      title: `施術まとめ一覧 | ${SITE_NAME}`,
      description:
        '二重整形・鼻整形・脂肪吸引など、施術ごとの実体験口コミまとめ。満足度・ダウンタイム・経過症例を比較できます。',
      canonicalPath: '/procedures',
    };
  }
  if (pathname.startsWith('/procedure/')) {
    const name = decodePathSegment(pathname, '/procedure/').slice(0, 40);
    if (name) {
      return {
        title: `${name}の口コミ・実体験まとめ | ${SITE_NAME}`,
        description: `${name}の美容医療口コミを実体験データから集計。満足度・ダウンタイム・経過症例をウラカルテで確認できます。`,
        canonicalPath: `/procedure/${encodeURIComponent(name)}`,
      };
    }
  }
  if (pathname === '/timelines') {
    const q = query.trim().slice(0, 40);
    if (q) {
      return {
        title: `${q}の経過タイムライン｜施術直後から数か月後まで | ${SITE_NAME}`,
        description: `${q}を受けた人の投稿を時系列でまとめた経過タイムライン。施術直後・1か月後・半年後のリアルを、同じ人の報告で追えます。`,
        canonicalPath: `/timelines?q=${encodeURIComponent(q)}`,
      };
    }
    return {
      title: `経過タイムライン｜同じ人の施術直後→その後を追える | ${SITE_NAME}`,
      description:
        '美容医療を受けた人のXの投稿を時系列でまとめた経過タイムライン。施術直後だけでなく、1か月後・半年後のリアルを同じ人の報告で読めます。',
      canonicalPath: '/timelines',
    };
  }
  if (pathname.startsWith('/timeline/')) {
    const author = decodePathSegment(pathname, '/timeline/').slice(0, 40);
    if (author) {
      return {
        title: `@${author} さんの経過タイムライン | ${SITE_NAME}`,
        description: `@${author} さんの美容医療の経過を、投稿の時系列でまとめています。施術直後からその後までの変化を追えます。`,
        canonicalPath: `/timeline/${encodeURIComponent(author)}`,
      };
    }
  }
  if (pathname.startsWith('/clinic/')) {
    const name = decodePathSegment(pathname, '/clinic/').slice(0, 40);
    if (name) {
      return {
        title: `${name}の口コミ・評判まとめ | ${SITE_NAME}`,
        description: `${name}の美容クリニック口コミを、広告・ステマを除いた実体験から集計しています。`,
        canonicalPath: `/clinic/${encodeURIComponent(name)}`,
      };
    }
  }
  if (pathname === '/search') {
    if (query.trim()) {
      const q = query.trim().slice(0, 40);
      return {
        title: `「${q}」の美容口コミ検索 | ${SITE_NAME}`,
        description: `「${q}」に関する美容・スキンケアの口コミをウラカルテで検索。エビデンス付きの信頼できるレビューをチェック。`,
        canonicalPath: `/search?query=${encodeURIComponent(q)}`,
        robots: 'noindex, follow',
      };
    }
    return {
      title: `美容口コミ検索 | ${SITE_NAME}`,
      description: DEFAULT_DESCRIPTION,
      canonicalPath: '/search',
      robots: 'noindex, follow',
    };
  }
  if (pathname === '/insights') {
    return {
      title: `まとめ記事 | ${SITE_NAME}`,
      description:
        'Xの美容口コミを集計したまとめ記事。ダウンタイム・満足度・よく名前が出る院など、広告では見えない傾向をウラカルテがまとめています。',
      canonicalPath: '/insights',
    };
  }
  if (pathname.startsWith('/insights/')) {
    const slug = decodePathSegment(pathname, '/insights/');
    if (slug) {
      const article = getInsightsArticle(slug);
      if (article) {
        return {
          title: `${article.title} | ${SITE_NAME}`,
          description: article.description,
          canonicalPath: `/insights/${article.slug}`,
        };
      }
      return {
        title: `まとめ記事 | ${SITE_NAME}`,
        description:
          'Xの美容口コミを集計したまとめ記事。広告では見えない傾向をウラカルテがまとめています。',
        canonicalPath: `/insights/${encodeURIComponent(slug)}`,
      };
    }
  }
  if (pathname === '/clinics') {
    return {
      title: `クリニック一覧 | ${SITE_NAME}`,
      description:
        'Xの投稿でタグ付けされた美容クリニックの一覧。投稿数・満足度・関連キーワードから、実体験ベースでクリニックを比較できます。',
      canonicalPath: '/clinics',
    };
  }
  if (pathname.startsWith('/post/')) {
    return {
      title: `口コミ詳細 | ${SITE_NAME}`,
      description: 'ウラカルテで見つけた美容口コミ。エビデンスバッジで信頼性を確認できます。',
      canonicalPath: pathname,
    };
  }
  if (pathname === '/karte/new') {
    return {
      title: `術後カルテをつくる | ${SITE_NAME}`,
      description: 'ボタンで答えるだけで、施術の経過レポを術後カルテにまとめられます。',
      canonicalPath: '/karte/new',
      robots: 'noindex, follow',
    };
  }
  if (pathname.startsWith('/karte/')) {
    return {
      title: `術後カルテ | ${SITE_NAME}`,
      description: '施術を受けた本人が書いた、経過・痛み・ダウンタイムの術後カルテ。',
      canonicalPath: pathname,
    };
  }
  if (pathname.startsWith('/admin')) {
    return {
      title: `管理者 | ${SITE_NAME}`,
      description: DEFAULT_DESCRIPTION,
      robots: 'noindex, nofollow',
    };
  }
  return { title: DEFAULT_TITLE, description: DEFAULT_DESCRIPTION, canonicalPath: pathname || '/' };
}

function upsertCanonical(href) {
  if (typeof document === 'undefined') return;
  let el = document.querySelector('link[rel="canonical"]');
  if (!href) return;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function applyCustomPageMeta({ title, description, canonicalPath, robots } = {}) {
  if (typeof document === 'undefined') return;
  if (title) document.title = title;
  if (robots) upsertMeta('meta[name="robots"]', 'name', 'robots', robots);
  if (description) {
    upsertMeta('meta[name="description"]', 'name', 'description', description);
    upsertMeta('meta[property="og:description"]', 'property', 'og:description', description);
    upsertMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);
  }
  if (title) {
    upsertMeta('meta[property="og:title"]', 'property', 'og:title', title);
    upsertMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title);
  }
  if (canonicalPath) {
    const abs = buildAbsoluteUrl(canonicalPath);
    upsertCanonical(abs);
    upsertMeta('meta[property="og:url"]', 'property', 'og:url', abs);
  }
}

export function applyPageMeta(pathname, search = '') {
  if (typeof document === 'undefined') return;
  const { title, description, canonicalPath, robots } = resolveMeta(pathname, search);
  document.title = title;
  upsertMeta('meta[name="description"]', 'name', 'description', description);
  upsertMeta('meta[property="og:title"]', 'property', 'og:title', title);
  upsertMeta('meta[property="og:description"]', 'property', 'og:description', description);
  upsertMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title);
  upsertMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);
  upsertMeta('meta[property="og:site_name"]', 'property', 'og:site_name', SITE_NAME);
  upsertMeta('meta[name="robots"]', 'name', 'robots', robots || 'index, follow');
  const ogUrl = buildAbsoluteUrl(canonicalPath || `${pathname}${search || ''}`);
  upsertCanonical(ogUrl);
  upsertMeta('meta[property="og:url"]', 'property', 'og:url', ogUrl);
  upsertMeta('meta[property="og:image"]', 'property', 'og:image', buildOgImageUrl());
  upsertMeta('meta[property="og:image:secure_url"]', 'property', 'og:image:secure_url', buildOgImageUrl());
  upsertMeta('meta[name="twitter:image"]', 'name', 'twitter:image', buildOgImageUrl());
}

export function buildServiceShareText() {
  return '美容口コミの信頼性をエビデンスで可視化する「ウラカルテ」';
}

export function buildTwitterShareUrl({ text, url }) {
  const params = new URLSearchParams();
  if (text) params.set('text', text);
  if (url) params.set('url', url);
  return `https://x.com/intent/tweet?${params.toString()}`;
}
